import { useEffect, useState, useRef } from "react";

interface ScrollTrackingConfig {
  thresholds?: number[]; // Percentages to track (default: [25, 50, 75, 100])
  onThresholdReached?: (threshold: number) => void;
}

export const useScrollTracking = (config: ScrollTrackingConfig = {}) => {
  const { 
    thresholds = [25, 50, 75, 100],
    onThresholdReached 
  } = config;
  
  const [reachedThresholds, setReachedThresholds] = useState<number[]>([]);
  const [scrollPercentage, setScrollPercentage] = useState(0);
  const trackedThresholds = useRef<Set<number>>(new Set());

  useEffect(() => {
    let ticking = false;

    const calculateScrollPercentage = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      
      const scrollableHeight = documentHeight - windowHeight;
      const percentage = scrollableHeight > 0 
        ? Math.min((scrollTop / scrollableHeight) * 100, 100)
        : 0;

      return Math.round(percentage);
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentPercentage = calculateScrollPercentage();
          setScrollPercentage(currentPercentage);

          // Check thresholds
          thresholds.forEach(threshold => {
            if (
              currentPercentage >= threshold && 
              !trackedThresholds.current.has(threshold)
            ) {
              trackedThresholds.current.add(threshold);
              setReachedThresholds(prev => [...prev, threshold]);
              
              // Log to console
              console.log(`📊 Scroll-Tiefe erreicht: ${threshold}%`);
              
              // Call callback if provided
              if (onThresholdReached) {
                onThresholdReached(threshold);
              }
            }
          });

          ticking = false;
        });
        
        ticking = true;
      }
    };

    // Initial check
    handleScroll();

    // Add scroll listener
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [thresholds, onThresholdReached]);

  return {
    scrollPercentage,
    reachedThresholds,
  };
};
